import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import useProducts from "./hooks/useProducts";
import { AoType, Bundle, Product } from "./types";
import useProductBundlesApi from "../../hooks/useProductBundlesApi";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { useNotificationContext } from "cerulean/src/NotificationContext";

interface ProductListManagerContextInterface {
  uatProducts: any[];
  products: any[];
  productsForType: (type: AoType) => Product[];
  staffLedProductsForType: (type: AoType) => Product[];
  consumerBundles: Bundle[];
  businessBundles: Bundle[];
  bundles: Bundle[];
  isBundlesLoaded: boolean;
  updateBundle: CallableFunction;
  updateProduct: CallableFunction;
  deleteProduct: CallableFunction;
  isCaoEnabled: boolean;
  isBaoEnabled: boolean;
  isConsumerStaffLedEnabled: boolean;
  isBusinessStaffLedEnabled: boolean;
  isProductBundlesEnabled: boolean;
  isHsaEnabled: boolean;
  isIraEnabled: boolean;
  canEditProducts: boolean;
  canAddProductBundles: boolean;
  canEditProductBundles: boolean;
  currentUserIsNarmi: boolean;
  allowDebitCardRequiredByProduct: boolean;
}
const ProductListManagerContext = createContext<ProductListManagerContextInterface>({
  uatProducts: [],
  products: [],
  productsForType: () => [],
  staffLedProductsForType: () => [],
  consumerBundles: [],
  businessBundles: [],
  bundles: [],
  isBundlesLoaded: false,
  updateBundle: () => null,
  updateProduct: () => null,
  deleteProduct: () => null,
  isCaoEnabled: false,
  isBaoEnabled: false,
  isConsumerStaffLedEnabled: false,
  isBusinessStaffLedEnabled: false,
  isProductBundlesEnabled: false,
  isIraEnabled: false,
  isHsaEnabled: false,
  canEditProducts: false,
  canAddProductBundles: false,
  canEditProductBundles: false,
  currentUserIsNarmi: false,
  allowDebitCardRequiredByProduct: false,
});

export const useProductListManagerContext = () => useContext(ProductListManagerContext);

const ProductListManagerContainer = ({ children }: PropsWithChildren) => {
  const { sendNegativeNotification } = useNotificationContext();
  const {
    products,
    productsForType,
    staffLedProductsForType,
    updateProduct,
    deleteProduct,
    uatProducts,
    allowDebitCardRequiredByProduct,
  } = useProducts();

  const { currentUser, features, permissions } = useUserConfiguration();
  const isCaoEnabled = features.includes("new_account_opening");
  const isBaoEnabled = features.includes("business_account_opening");
  const isConsumerStaffLedEnabled = features.includes("staffer_led_ao_consumer");
  const isBusinessStaffLedEnabled = features.includes("staffer_led_ao_business");
  const isProductBundlesEnabled = features.includes("product_bundles");
  const isHsaEnabled = features.includes("hsa");
  const isIraEnabled = features.includes("ira");

  const canEditProducts = permissions.includes("indigo.change_opening_products");
  const canViewProductBundles = permissions.includes("indigo.view_productbundle");
  const canAddProductBundles = permissions.includes("indigo.add_productbundle");
  const canEditProductBundles = permissions.includes("indigo.change_productbundle");

  const currentUserIsNarmi = currentUser.is_narmi;

  const { getAll } = useProductBundlesApi();
  const [bundles, setBundles] = useState<{ data: Bundle[]; loaded: boolean }>({
    data: [],
    loaded: false,
  });
  const fetchBundles = async () => {
    if (!isProductBundlesEnabled || !canViewProductBundles) {
      return;
    }
    const response = await getAll();
    if (!response.ok) {
      sendNegativeNotification(
        "There was an error loading product bundles. Please contact support."
      );
    }
    const responseJson = await response.json();
    setBundles({ data: responseJson.results, loaded: true });
  };
  useEffect(() => {
    fetchBundles();
  }, []);
  const consumerBundles = bundles.data.filter((b) => !b.is_business);
  const businessBundles = bundles.data.filter((b) => b.is_business);

  const updateBundle = (bundle: Bundle) => {
    setBundles((prev) => {
      const index = prev.data.findIndex((b) => b.uuid === bundle.uuid);
      if (index === -1) return prev;
      return {
        ...prev,
        data: [...prev.data.slice(0, index), bundle, ...prev.data.slice(index + 1)],
      };
    });
  };

  const value = {
    uatProducts,
    products,
    productsForType,
    staffLedProductsForType,
    consumerBundles,
    businessBundles,
    bundles: bundles.data,
    isBundlesLoaded: bundles.loaded,
    updateBundle,
    updateProduct,
    deleteProduct,
    isCaoEnabled,
    isBaoEnabled,
    isConsumerStaffLedEnabled,
    isBusinessStaffLedEnabled,
    isProductBundlesEnabled,
    isHsaEnabled,
    isIraEnabled,
    canEditProducts,
    canAddProductBundles,
    canEditProductBundles,
    currentUserIsNarmi,
    allowDebitCardRequiredByProduct,
  };

  return (
    <ProductListManagerContext.Provider value={value}>
      {children}
    </ProductListManagerContext.Provider>
  );
};

export default ProductListManagerContainer;
